import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { TrendingUp, Users, AlertCircle, Euro, Loader2, AlertTriangle, Package as PackageIcon, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_COLORS = {
  costo: "#ef4444",
  non_uso: "#f59e0b",
  feature_mancanti: "#6366f1",
  bug_tecnici: "#ec4899",
  altro_gestionale: "#06b6d4",
  chiusura: "#64748b",
  altro: "#94a3b8",
};

const KpiCard = ({ icon: Icon, label, value, suffix = "", tone = "indigo", testId, subtitle }) => {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</span>
      </div>
      <p className="text-3xl font-black text-gray-900">{value}<span className="text-base font-semibold text-gray-400 ml-1">{suffix}</span></p>
      {subtitle && <p className="text-[11px] text-gray-400 mt-1.5 font-medium">{subtitle}</p>}
    </div>
  );
};

const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [churnList, setChurnList] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("sa_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [overview, churn] = await Promise.all([
        axios.get(`${API}/superadmin/analytics/overview`, { headers }),
        axios.get(`${API}/superadmin/analytics/churn-list`, { headers }),
      ]);
      setData(overview.data);
      setChurnList(churn.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
    </div>
  );
  if (!data) return <p className="text-sm text-gray-500">Nessun dato disponibile</p>;

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-black text-gray-900">Analytics</h2>
        <p className="text-sm text-gray-500 mt-1">Una panoramica veloce su attivazioni, abbonamenti attivi e motivi di churn.</p>
      </div>

      {/* ── Churn critical alert ── */}
      {data.churn_alert && (
        <div className="bg-gradient-to-r from-red-500 to-rose-600 rounded-2xl p-5 text-white shadow-lg flex items-start gap-4" data-testid="churn-alert-full">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-white animate-pulse" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Allerta churn</p>
            <h3 className="text-lg font-black mt-0.5">Stessa motivazione ricorrente nelle ultime disdette</h3>
            <p className="text-sm opacity-90 mt-1">{data.churn_alert.message}</p>
            <p className="text-xs opacity-70 mt-2">Soglia: {data.churn_alert.threshold}+ disdette in 30 giorni con stesso motivo. Considera di prioritizzare interventi su questa area.</p>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={TrendingUp} label="Attivazioni mese" value={data.attivazioni_mese} tone="emerald" testId="kpi-attivazioni" />
        <KpiCard icon={Users} label="Abbonamenti attivi" value={data.attivi} tone="indigo" testId="kpi-attivi"
          subtitle={`${data.attivi_paganti} paganti · ${data.attivi_trial} in trial`} />
        <KpiCard icon={AlertCircle} label="Disdette 30gg" value={data.disdette_30gg} tone="rose" testId="kpi-disdette" />
        <KpiCard icon={Euro} label="MRR stimato" value={Math.round(data.mrr_stimato)} suffix="€" tone="amber" testId="kpi-mrr" />
      </div>

      {/* Users KPI Row */}
      {data.utenti && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Users} label="Utenti totali" value={data.utenti.totali} tone="indigo" testId="kpi-utenti-totali"
            subtitle={`${data.utenti.admin} admin · ${data.utenti.operatori} operatori`} />
          <KpiCard icon={UserCheck} label="Utenti attivi" value={data.utenti.in_societa_attive} tone="emerald" testId="kpi-utenti-attivi"
            subtitle="In società con abbonamento attivo" />
          <KpiCard icon={Users} label="Media utenti / società" value={data.utenti.media_per_societa} tone="indigo" testId="kpi-media-utenti"
            subtitle="Indicatore adoption" />
          <KpiCard icon={PackageIcon} label="Pacchetti venduti" value={data.top_packages?.length || 0} tone="amber" testId="kpi-pacchetti-count"
            subtitle="Tipologie attive sul mercato" />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attivazioni 30gg */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm lg:col-span-2" data-testid="chart-attivazioni">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Attivazioni ultimi 30 giorni</h3>
              <p className="text-xs text-gray-400 mt-0.5">Nuove società registrate per giorno</p>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={data.attivazioni_30gg}>
                <defs>
                  <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={4} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }}
                  formatter={(v) => [`${v} attivazioni`, ""]}
                  labelFormatter={(l) => `Giorno ${l}`}
                />
                <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} fill="url(#actGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Motivi disdetta */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid="chart-motivi">
          <h3 className="text-sm font-bold text-gray-900 mb-1">Motivi disdetta</h3>
          <p className="text-xs text-gray-400 mb-3">Aggregato {data.totale_disdette_con_motivo} disdette</p>
          {data.motivi_disdetta.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p className="text-xs">Ancora nessuna disdetta registrata</p>
            </div>
          ) : (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.motivi_disdetta} dataKey="value" nameKey="label" innerRadius={42} outerRadius={70} paddingAngle={2}>
                    {data.motivi_disdetta.map((m) => (
                      <Cell key={m.key} fill={REASON_COLORS[m.key] || "#94a3b8"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Pacchetti più venduti */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="top-packages">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Pacchetti più venduti</h3>
            <p className="text-xs text-gray-400 mt-0.5">Ranking tra gli abbonamenti attivi · MRR per pacchetto</p>
          </div>
          <PackageIcon className="h-4 w-4 text-gray-300" />
        </div>
        {!data.top_packages || data.top_packages.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">Nessun pacchetto attivo.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.top_packages.map((pkg, i) => {
              const maxCount = data.top_packages[0].count || 1;
              const pct = (pkg.count / maxCount) * 100;
              const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
              return (
                <div key={pkg.id} className="px-5 py-3 hover:bg-gray-50 transition-colors" data-testid={`top-pkg-${i}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-sm font-bold text-gray-500 w-10 flex-shrink-0">{medal}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{pkg.nome}</p>
                        <p className="text-[11px] text-gray-400">{pkg.prezzo_mensile.toFixed(2)}€/mese · cat. {pkg.tipo}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <p className="text-sm font-black text-gray-900">{pkg.count} <span className="text-[11px] font-semibold text-gray-400">vendite</span></p>
                      <p className="text-[11px] font-semibold text-emerald-700">€{pkg.mrr.toFixed(2)} MRR</p>
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden ml-[52px]">
                    <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tabella ultime disdette */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="churn-table">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Ultime disdette</h3>
            <p className="text-xs text-gray-400 mt-0.5">Dettaglio cliente, motivo e note libere</p>
          </div>
          <span className="text-xs font-semibold text-gray-500">{churnList?.total ?? 0} totali</span>
        </div>

        {!churnList || churnList.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">Nessuna disdetta registrata.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Cliente</th>
                  <th className="px-4 py-3 text-left font-semibold">Stato</th>
                  <th className="px-4 py-3 text-left font-semibold">Data richiesta</th>
                  <th className="px-4 py-3 text-left font-semibold">Fine contratto</th>
                  <th className="px-4 py-3 text-left font-semibold">Canone</th>
                  <th className="px-4 py-3 text-left font-semibold">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {churnList.items.map((row, i) => {
                  const dataReq = row.cancel_requested_at ? new Date(row.cancel_requested_at).toLocaleDateString("it-IT") : "—";
                  const dataFine = row.data_scadenza ? new Date(row.data_scadenza).toLocaleDateString("it-IT") : "—";
                  const isProgrammata = row.stato === "Disdetta programmata";
                  return (
                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`churn-row-${i}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900">{row.denominazione || "—"}</p>
                        <p className="text-xs text-gray-400">{row.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${isProgrammata ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                          {row.stato}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{dataReq}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{dataFine}</td>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-900">{row.canone_mensile ? `${row.canone_mensile.toFixed(2)}€` : "—"}</td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-gray-800">{row.motivo || <span className="text-gray-400">non specificato</span>}</p>
                        {row.note && <p className="text-[11px] text-gray-500 italic mt-0.5">"{row.note}"</p>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Mini Card riassuntiva (per pagina Abbonamenti) ─────────────────────────
export const AnalyticsMiniCard = ({ onOpen }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("sa_token");
    axios.get(`${API}/superadmin/analytics/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <div
      onClick={onOpen}
      className="group relative overflow-hidden rounded-3xl bg-indigo-950 p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-indigo-900/50"
      data-testid="analytics-mini-card"
      style={{ color: 'white' }}
    >
      {/* Background texture/glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/40 via-transparent to-violet-600/40 opacity-80" />
      <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl group-hover:bg-violet-500/30 transition-colors" />

      {/* Churn alert banner */}
      {data.churn_alert && (
        <div
          className="absolute top-0 left-0 right-0 bg-red-600/90 backdrop-blur-md px-4 py-2.5 flex items-center justify-center gap-2 z-10"
          data-testid="churn-alert-banner"
          style={{ color: 'white' }}
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0 animate-pulse" style={{ color: 'white' }} />
          <p className="text-xs font-bold truncate" style={{ color: 'white' }}>
            Churn critico: {data.churn_alert.count} disdette per "{data.churn_alert.label}"
          </p>
        </div>
      )}

      <div className={`relative z-10 flex items-center justify-between mb-6 ${data.churn_alert ? "mt-8" : ""}`}>
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
            <p className="text-xs font-black uppercase tracking-widest" style={{ color: '#c7d2fe' }}>Analytics</p>
          </div>
          <p className="text-base font-bold flex items-center gap-1" style={{ color: 'white' }}>
            Apri dashboard <span style={{ color: '#818cf8' }}>→</span>
          </p>
        </div>
        <div className="h-11 w-11 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-md border border-white/10">
          <TrendingUp className="h-5 w-5" style={{ color: 'white' }} />
        </div>
      </div>

      <div className="relative z-10 grid grid-cols-3 gap-4 mb-4">
        <div className="flex flex-col">
          <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: 'rgba(199,210,254,0.8)' }}>Attivazioni mese</p>
          <p className="text-3xl font-black tracking-tighter" style={{ color: 'white' }}>{data.attivazioni_mese}</p>
        </div>
        <div className="flex flex-col">
          <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: 'rgba(199,210,254,0.8)' }}>Attivi</p>
          <p className="text-3xl font-black tracking-tighter" style={{ color: 'white' }}>{data.attivi}</p>
        </div>
        <div className="flex flex-col">
          <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: 'rgba(253,164,175,0.8)' }}>Disdette 30gg</p>
          <p className="text-3xl font-black tracking-tighter" style={{ color: 'white' }}>{data.disdette_30gg}</p>
        </div>
      </div>

      {data.attivazioni_30gg.length > 0 && (
        <div style={{ width: "100%", height: 55 }} className="relative z-10 mt-3">
          <ResponsiveContainer>
            <AreaChart data={data.attivazioni_30gg}>
              <defs>
                <linearGradient id="miniGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fff" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#fff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="count" stroke="#fff" strokeWidth={2} fill="url(#miniGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPanel;
